<template>
  <div class="travel-buddy-simulator-tab p-6">
    <!--div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold text-blue-900 mb-3">
        <i class="pi pi-users mr-2"></i>
        Travel Buddy Simulator
      </h2>
      <div class="text-blue-800 space-y-2">
        <p>
          This tool processes up to 10 random travel buddy meetings with status
          <code class="bg-blue-100 px-2 py-1 rounded">created</code> in a realistic way:
          some are <strong>skipped</strong>, some <strong>completed</strong> with only one user submitting feedback,
          then optionally backfills the second user’s feedback on some completed meetings. Finally it runs the matching algorithm.
        </p>
        <p><strong>Steps:</strong></p>
        <ul class="list-disc list-inside ml-4 space-y-1">
          <li>Fetch up to 10 random meetings with status &quot;created&quot;</li>
          <li>For each: randomly <strong>skip</strong> (~25%) or <strong>complete</strong>; when completing, only one member submits feedback</li>
          <li>Check completed meetings (including existing) for missing second feedback and backfill some (~50%)</li>
          <li>Run the travel buddy matching algorithm</li>
          <li>Display newly created meetings with member names and category</li>
        </ul>
        <p class="mt-2 text-sm text-blue-700">
          <strong>Meeting category:</strong> Same workshop, Different workshop, or Company wide (at least one member not in any workshop).
        </p>
        <p class="mt-2 text-sm text-blue-700">
          <strong>In-memory (no DB):</strong> Run the full algorithm in memory until every pair has at least one completed meeting; displays <strong>all matched partners</strong> so you can verify no tenant-wide duplication.
        </p>
      </div>
    </div-->

    <!-- Mode: Simulate (writes DB) vs In-memory (no DB) -->
    <div class="flex flex-wrap items-center gap-4 mb-6">
      <div class="flex items-center gap-2">
        <label class="font-medium text-gray-700">Mode:</label>
        <SelectButton v-model="mode" :options="modeOptions" option-label="label" option-value="value" />
      </div>
      <template v-if="mode === 'inMemory'">
        <div class="flex items-center gap-2">
          <label class="font-medium text-gray-700">Skip probability (0–1):</label>
          <InputNumber v-model="skipProbability" :min="0" :max="1" :step="0.05" :min-fraction-digits="2" />
        </div>
        <Button
          label="Run in-memory simulation"
          icon="pi pi-play"
          :loading="loading"
          @click="handleInMemorySimulate"
          class="p-button-secondary"
        />
      </template>
      <template v-else>
        <Button
          label="Simulate"
          icon="pi pi-play"
          :loading="loading"
          @click="handleSimulate"
          class="p-button-primary"
        />
      </template>
    </div>

    <!-- In-memory simulation result: all matched partners + verification -->
    <div v-if="mode === 'inMemory' && inMemoryResult" class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        <i class="pi pi-list mr-2"></i>
        In-memory simulation results
      </h3>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">Total rounds</div>
          <div class="text-2xl font-bold text-gray-800">{{ inMemoryResult.summary?.totalRounds ?? 0 }}</div>
        </div>
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">Total meetings</div>
          <div class="text-2xl font-bold text-gray-800">{{ inMemoryResult.summary?.totalMeetings ?? 0 }}</div>
        </div>
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">Completed / Skipped</div>
          <div class="text-2xl font-bold text-gray-800">{{ inMemoryResult.summary?.completedCount ?? 0 }} / {{ inMemoryResult.summary?.skippedCount ?? 0 }}</div>
        </div>
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">Pairs with ≥1 completed</div>
          <div class="text-2xl font-bold text-gray-800">{{ inMemoryResult.summary?.uniquePairsWithCompletedMeeting ?? 0 }} / {{ inMemoryResult.summary?.expectedPairsForFullCoverage ?? 0 }}</div>
        </div>
      </div>
      <div class="mb-4 p-3 rounded border"
        :class="inMemoryDuplicationCheck.ok ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-300'">
        <span v-if="inMemoryDuplicationCheck.ok" class="text-green-800 font-medium">
          <i class="pi pi-check-circle mr-1"></i> No tenant-wide duplication: every pair has at most one completed meeting; full coverage reached.
        </span>
        <template v-else>
          <span class="text-amber-800 font-medium">
            <i class="pi pi-exclamation-triangle mr-1"></i> {{ inMemoryDuplicationCheck.message }}
          </span>
        </template>
      </div>
      <h4 class="text-md font-semibold text-gray-700 mb-2">All matched partners by user</h4>
      <Accordion :multiple="true" class="border border-gray-200 rounded">
        <AccordionPanel v-for="user in inMemoryMatchesByUser" :key="user.userId" :value="user.userId">
          <AccordionHeader>
            <span class="font-medium">{{ user.userName || user.userId }}</span>
            <span class="text-gray-500 text-sm ml-2">({{ user.matches.length }} meeting{{ user.matches.length !== 1 ? 's' : '' }})</span>
          </AccordionHeader>
          <AccordionContent>
            <ul class="list-none space-y-2 pl-0">
              <li
                v-for="(m, idx) in user.matches"
                :key="`${user.userId}-${m.buddyId}-${m.roundIndex}-${idx}`"
                class="flex flex-wrap items-center gap-2 py-1.5 border-b border-gray-100 last:border-0"
              >
                <span class="text-gray-700">Matched with <strong>{{ m.buddyName || m.buddyId }}</strong></span>
                <span class="text-gray-500 text-sm">— Round {{ m.roundIndex }}</span>
                <Tag :value="m.status" :severity="m.status === 'completed' ? 'success' : 'warn'" />
              </li>
            </ul>
          </AccordionContent>
        </AccordionPanel>
      </Accordion>
    </div>

    <div v-if="result && mode !== 'inMemory'" class="bg-white border border-gray-200 rounded-lg p-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">
        <i class="pi pi-list mr-2"></i>
        Results
      </h3>
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">Completed</div>
          <div class="text-2xl font-bold text-gray-800">{{ result.completedCount ?? 0 }}</div>
        </div>
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">Skipped</div>
          <div class="text-2xl font-bold text-gray-800">{{ result.skippedCount ?? 0 }}</div>
        </div>
        <div class="bg-gray-50 rounded p-4">
          <div class="text-sm text-gray-600">New meetings created</div>
          <div class="text-2xl font-bold text-gray-800">{{ result.newMeetings?.length ?? 0 }}</div>
        </div>
      </div>
      <div v-if="(result.oneSidedFeedbackCount ?? 0) > 0 || (result.secondFeedbackBackfilledCount ?? 0) > 0" class="mb-4 text-sm text-gray-600">
        <span v-if="(result.oneSidedFeedbackCount ?? 0) > 0">Completed with one user feedback only: {{ result.oneSidedFeedbackCount }}.</span>
        <span v-if="(result.secondFeedbackBackfilledCount ?? 0) > 0"> Second user feedback backfilled: {{ result.secondFeedbackBackfilledCount }}.</span>
      </div>

      <!-- Uniqueness check: all pairs should be unique (no duplicate pair in this run) -->
      <div v-if="result.newMeetings && result.newMeetings.length > 0" class="mb-4 p-3 rounded border"
        :class="pairUniqueness.unique ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-300'">
        <span v-if="pairUniqueness.unique" class="text-green-800 font-medium">
          <i class="pi pi-check-circle mr-1"></i> All pairs unique — no duplicate meetings in this run.
        </span>
        <template v-else>
          <span class="text-amber-800 font-medium">
            <i class="pi pi-exclamation-triangle mr-1"></i> Duplicate pair(s) in this run:
          </span>
          <span class="text-amber-700 ml-1">{{ pairUniqueness.duplicatePairLabels.join('; ') }}</span>
        </template>
      </div>

      <div v-if="result.newMeetings && result.newMeetings.length > 0">
        <DataTable :value="result.newMeetings" :paginator="false" responsive-layout="scroll" class="p-datatable-sm">
          <Column field="members" header="Members">
            <template #body="{ data }">
              {{ memberNames(data.members) }}
            </template>
          </Column>
          <Column field="category" header="Category">
            <template #body="{ data }">
              <Tag :value="categoryLabel(data.category)" :severity="categorySeverity(data.category)" />
            </template>
          </Column>
        </DataTable>
      </div>
      <p v-else class="text-gray-600">
        No new meetings were created by the matching run.
      </p>
      <div v-if="result.hint" class="mt-4 p-3 rounded border bg-amber-50 border-amber-200 text-amber-800 text-sm">
        <i class="pi pi-info-circle mr-1"></i> {{ result.hint }}
      </div>
    </div>

    <ConfirmDialog group="travel-buddy-simulator" />
    <Toast />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import SelectButton from 'primevue/selectbutton';
import InputNumber from 'primevue/inputnumber';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';

export default {
  name: 'TravelBuddySimulatorTab',
  components: {
    Button,
    ConfirmDialog,
    DataTable,
    Column,
    Tag,
    Toast,
    SelectButton,
    InputNumber,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
  },
  data() {
    return {
      result: null,
      inMemoryResult: null,
      mode: 'db',
      modeOptions: [
        { label: 'In-memory (no DB)', value: 'inMemory' },
      ],
      skipProbability: 0.25,
    };
  },
  computed: {
    ...mapState('scheduler', ['loading']),
    /** Group in-memory result by user: each user has a list of matches (buddy name, round, status). */
    inMemoryMatchesByUser() {
      const pairs = this.inMemoryResult?.allMatchedPairs || [];
      const byUser = new Map(); // userId -> { userId, userName, matches: [{ buddyId, buddyName, roundIndex, status }] }
      for (const p of pairs) {
        const add = (userId, userName, buddyId, buddyName, roundIndex, status) => {
          if (!byUser.has(userId)) byUser.set(userId, { userId, userName, matches: [] });
          byUser.get(userId).matches.push({ buddyId, buddyName, roundIndex, status });
        };
        add(p.userAId, p.userAName, p.userBId, p.userBName, p.roundIndex, p.status);
        add(p.userBId, p.userBName, p.userAId, p.userAName, p.roundIndex, p.status);
      }
      const list = Array.from(byUser.values());
      list.sort((a, b) => (a.userName || a.userId || '').localeCompare(b.userName || b.userId || ''));
      return list;
    },
    inMemoryDuplicationCheck() {
      const res = this.inMemoryResult;
      if (!res?.allMatchedPairs) return { ok: false, message: 'No data.' };
      const completed = (res.allMatchedPairs || []).filter((p) => p.status === 'completed');
      const pairKeys = new Set();
      let duplicates = 0;
      for (const p of completed) {
        const key = [p.userAId, p.userBId].sort().join('|');
        if (pairKeys.has(key)) duplicates++;
        pairKeys.add(key);
      }
      const expected = res.summary?.expectedPairsForFullCoverage ?? 0;
      const uniqueFromBackend = res.summary?.uniquePairsWithCompletedMeeting ?? 0;
      // Use backend summary for full coverage (it includes initial state); allMatchedPairs only has pairs completed in this run
      const fullCoverage = expected === 0 || uniqueFromBackend === expected;
      const noDuplicates = duplicates === 0;
      const ok = noDuplicates && fullCoverage;
      if (ok) return { ok: true, message: '' };
      if (duplicates > 0) return { ok: false, message: `Duplicate completed pair(s) found: ${duplicates}. Each pair should have at most one completed meeting.` };
      return { ok: false, message: `Full coverage not reached: ${uniqueFromBackend} unique pairs with completed meeting, expected ${expected}.` };
    },
    pairUniqueness() {
      const meetings = this.result?.newMeetings || [];
      if (meetings.length === 0) return { unique: true, duplicatePairLabels: [] };
      const seen = new Map(); // pairKey -> label (first occurrence)
      const duplicatePairLabels = [];
      for (const m of meetings) {
        const ids = (m.members || []).map((x) => x.id || x).filter(Boolean);
        if (ids.length < 2) continue;
        const key = [...ids].sort().join('|');
        const label = (m.members || []).map((x) => x.name || x.id || '—').join(' – ');
        if (seen.has(key)) {
          if (seen.get(key) !== null) {
            duplicatePairLabels.push(seen.get(key));
            seen.set(key, null);
          }
        } else {
          seen.set(key, label);
        }
      }
      return {
        unique: duplicatePairLabels.length === 0,
        duplicatePairLabels,
      };
    },
  },
  methods: {
    ...mapActions('scheduler', ['simulateTravelBuddy', 'runTravelBuddySimulation']),
    memberNames(members) {
      if (!Array.isArray(members)) return '—';
      return members.map((m) => m.name || m.id || '—').join(', ');
    },
    categoryLabel(category) {
      const labels = {
        same_workshop: 'Same workshop',
        different_workshop: 'Different workshop',
        company_wide: 'Company wide',
      };
      return labels[category] ?? category ?? '—';
    },
    categorySeverity(category) {
      const severities = {
        same_workshop: 'success',
        different_workshop: 'info',
        company_wide: 'warn',
      };
      return severities[category] ?? 'secondary';
    },
    handleSimulate() {
      this.$confirm.require({
        group: 'travel-buddy-simulator',
        message: 'This will process up to 10 random travel buddy meetings (some skipped, some completed with one user feedback), backfill second feedback where applicable, then run the matching algorithm. Continue?',
        header: 'Confirm Simulation',
        icon: 'pi pi-question-circle',
        accept: () => {
          this.inMemoryResult = null;
          this.simulateTravelBuddy()
            .then((body) => {
              this.result = body;
            })
            .catch(() => {
              this.result = null;
            });
        },
      });
    },
    handleInMemorySimulate() {
      this.result = null;
      const prob = Number(this.skipProbability);
      const skipProbability = Number.isFinite(prob) && prob >= 0 && prob <= 1 ? prob : 0.25;
      this.runTravelBuddySimulation(skipProbability)
        .then((body) => {
          this.inMemoryResult = body;
        })
        .catch(() => {
          this.inMemoryResult = null;
        });
    },
  },
};
</script>

<style scoped>
.travel-buddy-simulator-tab {
  max-width: 1200px;
}

code {
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}
</style>
